import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Receipt,
  ArrowDownToLine,
  ArrowUpFromLine,
  Boxes,
  ClipboardCheck,
  Users as UsersIcon,
  LogOut,
  Warehouse,
  Fuel,
  Zap,
  Settings,
  Contact,
  FileText,
  FileInput,
  ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/inward", label: "Inward", icon: ArrowDownToLine, testid: "nav-inward-link" },
  { to: "/outward", label: "Outward", icon: ArrowUpFromLine, testid: "nav-outward-link" },
  { to: "/items", label: "Items & Stock", icon: Boxes, testid: "nav-items-link" },
  { to: "/gatepass", label: "Gate Pass", icon: ClipboardCheck, testid: "nav-gatepass-link" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses-link" },
  { section: "Billing" },
  { to: "/parties", label: "Parties", icon: Contact, testid: "nav-parties-link" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testid: "nav-po-link" },
  { to: "/invoices", label: "Sales Invoices", icon: FileText, testid: "nav-invoices-link" },
  { to: "/vendor-invoices", label: "Vendor Invoices", icon: FileInput, testid: "nav-vendor-invoices-link" },
  { section: "Fuel & Power" },
  { to: "/diesel", label: "Diesel Ledger", icon: Fuel, testid: "nav-diesel-link" },
  { to: "/meters", label: "kWh Meters", icon: Zap, testid: "nav-meters-link" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = isAdmin
    ? [...links, { to: "/fuel-settings", label: "Settings", icon: Settings, testid: "nav-fuel-settings-link" },
       { section: "Admin" }, { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users-link" }]
    : links;
  const pageLinks = navItems.filter((l) => l.to);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-[hsl(222_39%_8%)] fixed h-screen">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-border">
          <div className="h-10 w-10 rounded-lg bg-sky-500 flex items-center justify-center">
            <Warehouse className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold leading-none">GateTrack</h1>
            <p className="text-[11px] text-muted-foreground mt-1 font-mono-sku">INVENTORY · EXPENSE · FUEL</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((l) => {
            if (l.section) return <p key={l.section} className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-mono-sku">{l.section}</p>;
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-sky-500/15 text-sky-400 border border-sky-500/30"
                      : "text-slate-400 hover:bg-white/5 hover:text-slate-100 border border-transparent"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-border">
          <div className="px-3 pb-3">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            <span
              className={`inline-block mt-2 text-[10px] font-mono-sku uppercase px-2 py-0.5 rounded ${
                isAdmin ? "bg-sky-500/20 text-sky-300" : "bg-slate-500/20 text-slate-300"
              }`}
              data-testid="user-role-badge"
            >
              {user?.role}
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-3 text-slate-400 hover:bg-rose-500/10 hover:text-rose-400"
          >
            <LogOut className="h-[18px] w-[18px]" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-[hsl(222_39%_8%)] border-b border-border flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-sky-500 flex items-center justify-center">
            <Warehouse className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold">GateTrack</span>
        </div>
        <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-btn-mobile">
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-[hsl(222_39%_8%)] border-t border-border flex overflow-x-auto">
        {pageLinks.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex-1 min-w-[64px] flex flex-col items-center gap-1 py-2 text-[10px] ${
                  isActive ? "text-sky-400" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {l.label.split(" ")[0]}
            </NavLink>
          );
        })}
      </nav>

      {/* Main */}
      <main className="flex-1 md:ml-64 pt-16 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
